import { OmitStrict } from "@amx/ux-twoui-utils";
import { IStackStyles, IStackTokens } from "@fluentui/react";
import { AriaAttributes } from "react";
import { ColorOverride } from "../styles/fonts";
import { throwOrReportComponentValidationError } from "./validation";

export interface BaseProps extends AriaAttributes {
    /**
     * Optional element ID
     */
    readonly id?: string;

    /**
     * Optional prop needed for narrator to choose the proper language
     */
    readonly lang?: string;
}

export interface AriaLabelRejectProps extends BaseProps {
    // aria-label is optional in AriaAttributes, making it forbidden here
    readonly "aria-label"?: undefined;
}

export interface AriaLabelEnforcedProps extends BaseProps {
    // aria-label is optional in AriaAttributes, making it mandatory here
    readonly "aria-label": string;
}

/**
 * The close button is used by dismissible components such as Dialog and NotificationBar.
 */
export interface CloseButtonProps extends OmitStrict<InteractiveElementProps, "disabled" | "allowDisabledFocus"> {
}

export interface InteractiveElementProps extends AriaLabelEnforcedProps {
    // Identifier sent to our telemetry pipeline when the element is interacted with
    readonly "data-bi-id": string;

    // Optional flag to indicate that the element is disabled
    readonly disabled?: boolean;

    // Optional flag to determine if the button can have focus in disabled mode
    readonly allowDisabledFocus?: boolean;
}

export interface ShimmerElementProps {
    /**
     * Indicate if data has been loaded or not.
     */
    readonly isLoaded?: boolean;
}

export interface IrisProps {
    /**
     * The URL used to generate an Iris impression beacon.
     * See also https://osgwiki.com/wiki/Iris_Insights_Beacons
     */
    readonly impressionBeaconUrl: string;

    /**
     * The URL used to generate an Iris click beacon.
     * See also https://osgwiki.com/wiki/Iris_Insights_Beacons
     */
    readonly clickBeaconUrl: string;

    /**
     * Send impression beacon only when the element is visible.
     */
    readonly impressionWhenVisible?: boolean;
}

export interface ImageDimensions {
    readonly height: string | number;
    readonly width: string | number;
}

interface InteractiveElementWithOnClicksProps {
    /**
     * Type of element interaction.
     */
    readonly interactionType: "withOnClick";
    /**
     * OnClick action to carry when the user clicks on the element.
     */
    readonly onClick: (ev?: React.MouseEvent<HTMLElement>) => void;
}

interface InteractiveElementWithHrefProps {
    /**
     * Type of interactionType interaction.
     */
    readonly interactionType: "withHref";
    /**
     * Hypertext reference where to direct the user when they click on the element.
     */
    readonly href: string;
}

export type InteractiveElementWithOnClickXorHrefProps = InteractiveElementWithOnClicksProps | InteractiveElementWithHrefProps;

export interface TextBaseProps extends BaseProps {
    /**
     * block is a boolean which can be either true or false that allows you to
     * display the text as a block
     */
    readonly block?: boolean;
    /**
     * nowrap is a boolean which can be either true or false that allows you to
     * remove wrapping from the text component
     */
    readonly nowrap?: boolean;
    /**
     * Color override options for text - currently just "error" which results in red text
     */
    readonly colorOverride?: ColorOverride;
}

// Setting the padding in the "styles" object causes console issues. Adding this to make it easier for a `getStyles` method to return both a styles, and a tokens object
export interface FluentStackStyleProps {
    readonly styles: IStackStyles;
    readonly tokens: IStackTokens;
}

const dataPrefix = "data-";
const ariaPrefix = "aria-";

// Unfortunately TypeScript compiler allows to pass in any prop with a hyphen in it.
// This method will check for allowed props with hyphen
export const validateProps = (componentName: string, props?: BaseProps): void => {
    const filterKeysWithPrefix = (prefix: string): void => {
        keysWithHyphen = keysWithHyphen.filter((key) => key.substr(0, prefix.length) !== prefix);
    };

    if (!props) {
        return;
    }

    let keysWithHyphen = Object.keys(props).filter((key) => key.indexOf("-") !== -1);

    // enforcement of aria-* is left to compiler/linter, through extending
    // the AriaAttributes interface
    filterKeysWithPrefix(ariaPrefix);

    // any data-* allowed
    filterKeysWithPrefix(dataPrefix);

    if (keysWithHyphen.length > 0) {
        throwOrReportComponentValidationError(componentName, `Invalid props: ${keysWithHyphen}`, 1);
    }
};

export type BooleanInputOnChangeProps = (
    event: React.MouseEvent<HTMLElement>,
    checked?: boolean
) => void;

export type OnClickType = React.MouseEventHandler<HTMLElement>;
